import { useEffect, useState } from "react";
import { collection, getDocs, query, where, Timestamp, onSnapshot, doc } from "firebase/firestore";
import { db } from "../../firebase";
import HeatmapYearly from "./HeatmapYearly";
import HeatmapMonthly from "./HeatmapMonthly";
import "./css/HeatmapPage.css";
import { useDispatch } from "react-redux";
import { showLoading, hideLoading } from "../../../redux/actions/loadingActions";
import { mostrarError } from "../../utils/Alertas";

const HeatmapPage: React.FC = () => {
    const dispatch = useDispatch();
    const [registros, setRegistros] = useState<{ fecha: string }[]>([]);
    const [modo, setModo] = useState<"year" | "month">("month");
    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState<number>(currentYear);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [dayLogs, setDayLogs] = useState<any[]>([]);
    const years = Array.from({ length: 10 }, (_, i) => currentYear + i);
    const [periodDates, setPeriodDates] = useState<Set<string>>(new Set());
    const [favoriteMode, setFavoriteMode] = useState(false);
    const [favoriteDates, setFavoriteDates] = useState<Set<string>>(new Set());

    useEffect(() => {
        dispatch(showLoading());
        const cargarLogs = async () => {

            const inicio = new Date(year, 0, 1);
            const fin = new Date(year, 11, 31, 23, 59, 59);

            const q = query(
                collection(db, "counter_logs"),
                where("createdAt", ">=", inicio),
                where("createdAt", "<=", fin)
            );

            const snap = await getDocs(q);

            const logs = snap.docs.map(doc => {
                const data = doc.data();
                const createdAt = data.createdAt.toDate();
                const fecha = createdAt.toLocaleDateString("sv-SE");
                return { fecha, favorite: data.favorite, createdAt };
            });

            setRegistros(logs);
            const favs = new Set(
                logs
                    .filter(l => l.favorite)
                    .map(l => l.fecha)
            );
            setFavoriteDates(favs);
            dispatch(hideLoading());
        };

        const cargarPeriodLogs = async () => {
            const q = query(collection(db, "period_logs"));
            const snap = await getDocs(q);

            const fechasPeriodo = new Set<string>();

            snap.docs.forEach(doc => {
                const data = doc.data();

                const start = parseFirestoreDate(data.startDate);
                const end = data.endDate ? parseFirestoreDate(data.endDate) : null;

                const dias = expandirPeriodo(start, end, year);
                dias.forEach(d => fechasPeriodo.add(d));
            });

            setPeriodDates(fechasPeriodo);
        };

        cargarLogs();
        cargarPeriodLogs();
    }, [year, dispatch]);

    useEffect(() => {
        const unsubCounter = onSnapshot(doc(db, "counter", "main"), (snap) => {
            const data = snap.data();
            if (data?.favoriteMode !== undefined) {
                setFavoriteMode(data.favoriteMode);
            }
        });
        return unsubCounter;
    }, []);

    const cargarLogsDelDia = async (date: Date) => {
        const inicio = new Date(date);
        inicio.setHours(0, 0, 0, 0);

        const fin = new Date(date);
        fin.setHours(23, 59, 59, 999);

        const q = query(
            collection(db, "counter_logs"),
            where("createdAt", ">=", Timestamp.fromDate(inicio)),
            where("createdAt", "<=", Timestamp.fromDate(fin))
        );

        const snap = await getDocs(q);

        setDayLogs(
            snap.docs.map(d => ({
                id: d.id,
                ...d.data()
            }))
        );

        setSelectedDate(date);
    };

    const cerrarPopover = () => {
        setSelectedDate(null);
        setDayLogs([]);
    };

    function parseFirestoreDate(value: any): Date {
        if (!value) {
            mostrarError("Fecha inválida")
        };

        // Firestore Timestamp
        if (value instanceof Timestamp) {
            return value.toDate();
        }

        // String tipo YYYY-MM-DD
        if (typeof value === "string") {
            return new Date(value + "T00:00:00");
        }

        // Date nativo
        if (value instanceof Date) {
            return value;
        }

        mostrarError("Formato de fecha no soportado");
        throw new Error("Formato de fecha no soportado");
    }

    function expandirPeriodo(
        start: Date,
        end: Date | null,
        year: number
    ): string[] {

        const fechas: string[] = [];

        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);

        const inicio = new Date(start);
        inicio.setHours(0, 0, 0, 0);

        // 🔥 Si no hay endDate → hasta hoy
        let fin = end ? new Date(end) : hoy;

        fin.setHours(0, 0, 0, 0);

        // Limitar al año actual del heatmap
        const inicioAno = new Date(year, 0, 1);
        const finAno = new Date(year, 11, 31);

        const inicioReal = inicio < inicioAno ? inicioAno : inicio;
        const finReal = fin > finAno ? finAno : fin;

        // Si el periodo no toca este año, no pintamos nada
        if (inicioReal > finReal) return [];

        let actual = new Date(inicioReal);

        while (actual <= finReal) {
            fechas.push(actual.toLocaleDateString("sv-SE"));
            actual.setDate(actual.getDate() + 1);
        }

        return fechas;
    }

    return (
        <div className="container-fluid">
            <div className="text-center">
                <h2 className="pb-3 page-heading">Mapa de calor</h2>
            </div>

            <div className="container-fluid content pb-3">
                <div className="heatmap-controls">
                    <div className="heatmap-year-selector">
                        <select
                            value={year}
                            onChange={(e) => setYear(Number(e.target.value))}
                        >
                            {years.map(y => (
                                <option key={y} value={y}>
                                    {y}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={`heatmap-switcher ${modo}`}>
                        <button onClick={() => setModo("month")}>Mensual</button>
                        <button onClick={() => setModo("year")}>Anual</button>
                        <span className="switch-indicator" />
                    </div>
                </div>


                {modo === "year" && (
                    <HeatmapYearly year={year} registros={registros} />
                )}

                {modo === "month" && (
                    <div onClick={cerrarPopover}>
                        <HeatmapMonthly
                            year={year}
                            registros={registros}
                            periodDates={periodDates}
                            activeDate={selectedDate}
                            dayLogs={dayLogs}
                            favoriteMode={favoriteMode}
                            favoriteDates={favoriteDates}
                            onDayClick={cargarLogsDelDia} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default HeatmapPage;

